"use client";

import ImageWrapper from "./ui/ImageWrapper";
import Image from "next/image";
import { TRACKING } from "@/lib/constants/tracking";
import SearchDropdownRevamp from "./ui/SearchDropdownRevamp";
import { buildUtilityPricingItems, SearchLocation } from "./ui/SearchDropdownRevamp";
import { useState, useRef, useEffect, useCallback } from "react";
import { SearchDropdownData } from "@/app/utilities/types/searchDropdown";
import { getGlobalSettings, meiliSearchApi, getSponsoredHighlights, getTrendingSearches } from "@/app/utilities/apis";
import { getLocationCookie } from "@/app/utilities/locationCookie";
import { DIWALI_HEADER } from "./DiwaliTheme";

const getActiveSearchLocation = async (): Promise<SearchLocation | null> => {
  if (typeof window === "undefined") {
    return null;
  }

  const location = getLocationCookie();
  const citySlug = location?.slug ?? "";

  try {
    const resolvedLocation = await getGlobalSettings(citySlug);
    if (!resolvedLocation?.name || !resolvedLocation.slug) {
      return null;
    }

    return {
      name: resolvedLocation.name,
      slug: resolvedLocation.slug,
    };
  } catch {
    if (!location?.name || !location.slug) {
      return null;
    }

    return {
      name: location.name,
      slug: location.slug,
    };
  }
};

const CDN_ICON = process.env.NEXT_PUBLIC_CDN_STATIC_URL;

type SearchHeaderProps = {
  searchDropdownData: SearchDropdownData;
};

const MobileSearchHeader = ({
  searchText,
  isLoading,
  onChange,
  onClear,
  onClose,
}: {
  searchText: string;
  isLoading: boolean;
  onChange: (val: string) => void;
  onClear: () => void;
  onClose: () => void;
}) => (
  <div className="flex items-center px-4 py-3">
    <div className="flex items-center flex-grow bg-[#F3F3F5] rounded-[8px] px-4 py-4">
      <button type="button" onClick={onClose} className="flex-shrink-0 mr-3">
        <Image
          src={`${CDN_ICON}search-icons/left-arrow-mob.svg`}
          alt="Back"
          width={23}
          height={23}
        />
      </button>

      <input
        type="text"
        placeholder="Search for Cars e.g Maruti"
        value={searchText}
        onChange={(e) => onChange(e.target.value)}
        className="flex-grow outline-none text-[14px] bg-transparent text-[#000] placeholder:text-[#717182]"
        autoFocus
      />

      {isLoading ? (
        <div className="animate-spin h-4 w-4 border-2 border-[#008C8B] border-t-transparent rounded-full flex-shrink-0" />
      ) : searchText.trim().length > 0 ? (
        <button
          type="button"
          onClick={onClear}
          className="p-1 bg-gray-400 rounded-full hover:bg-gray-500 transition-colors cursor-pointer flex-shrink-0"
          aria-label="Clear search"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3 w-3 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      ) : (
        <Image
          src={`${CDN_ICON}search-icons/search-green-arrow.svg`}
          alt="Search"
          width={20}
          height={20}
          title="Search"
        />
      )}
    </div>
  </div>
);

export default function SearchHeader({ searchDropdownData }: SearchHeaderProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [initialData, setInitialData] = useState<SearchDropdownData>(searchDropdownData);
  const [dropdownData, setDropdownData] = useState<SearchDropdownData>(searchDropdownData);
  const [activeLocation, setActiveLocation] = useState<SearchLocation | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const latestRequestIdRef = useRef(0);

  const blurActiveElement = useCallback(() => {
    if (typeof document === "undefined") return;

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
  }, []);

  const handleClose = useCallback(() => {
    blurActiveElement();
    setShowDropdown(false);
    setSearchText("");
    setIsSearchLoading(false);
    setIsUserTyping(false);
    setDropdownData(initialData);
  }, [blurActiveElement, initialData]);

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setIsSearchLoading(false);
    setIsUserTyping(false);
    setDropdownData(initialData);
  }, [initialData]);

  const handleSearchTextChange = useCallback((value: string) => {
    setSearchText(value);
    setIsUserTyping(value.trim().length >= 2);
  }, []);

  useEffect(() => {
    let mounted = true;

    const syncLocation = async () => {
      const nextLocation = await getActiveSearchLocation();
      if (mounted) {
        setActiveLocation(nextLocation);
      }
    };

    syncLocation();
    window.addEventListener("location-updated", syncLocation as EventListener);

    return () => {
      mounted = false;
      window.removeEventListener("location-updated", syncLocation as EventListener);
    };
  }, []);

  useEffect(() => {
    const fetchInitialDropdownData = async () => {
      try {
        const [sponsoredRes, trendingRes] = await Promise.all([
          getSponsoredHighlights(),
          getTrendingSearches()
        ]);

        const CDN_BASE = process.env.NEXT_PUBLIC_CDN_URL || 'https://assets.tractorjunction.com/car-junction/images/';

        const mappedSponsored = (sponsoredRes || []).map((item: any) => ({
          label: item.label || "Sponsored",
          title: item.title,
          subtitle: item.subtitle,
          img: item.image ? `${CDN_BASE}${item.image}` : `${CDN_BASE}placeholder-car.webp`,
          redirect_url: item.redirect_url || "/"
        }));

        const mappedTrending = (trendingRes || []).map((item: any) => ({
          title: item.title,
          redirect_url: item.redirect_url || "/"
        }));

        const updatedData: SearchDropdownData = {
          ...searchDropdownData,
          sponsoredData: mappedSponsored.length > 0 ? mappedSponsored : searchDropdownData.sponsoredData,
          trendingData: mappedTrending.length > 0 ? mappedTrending : searchDropdownData.trendingData
        };

        setInitialData(updatedData);
        setDropdownData((prev) => {
          if (searchText.trim().length < 2) {
            return updatedData;
          }
          return prev;
        });
      } catch (err) {
        console.error("Failed to load sponsored/trending searches:", err);
      }
    };

    fetchInitialDropdownData();
  }, [searchDropdownData]);

  useEffect(() => {
    const trimmedSearchText = searchText.trim();

    if (trimmedSearchText.length < 2) {
      setIsSearchLoading(false);
      setIsUserTyping(false);
      setDropdownData(initialData);
      return;
    }

    let isCancelled = false;
    const requestId = ++latestRequestIdRef.current;
    const fetchSearch = async () => {
      try {
        setIsUserTyping(false);
        setIsSearchLoading(true);
        const res = await meiliSearchApi(trimmedSearchText);
        if (
          isCancelled ||
          requestId !== latestRequestIdRef.current ||
          !res ||
          !res.success
        ) {
          return;
        }

        const nextData: SearchDropdownData = {
          icons: initialData.icons,
          sponsoredData: [...initialData.sponsoredData],
          trendingData: [...initialData.trendingData],
          sectionRanking: [...initialData.sectionRanking],
          step2Data: {
            models: [],
            utility: [],
            listings: [],
            comparisons: [],
            editorial: []
          }
        };

        const CDN_BASE = process.env.NEXT_PUBLIC_CDN_URL || 'https://assets.tractorjunction.com/car-junction/images/';

        if (res.type === "default") {
          if (res.data.sponsored?.length) {
            nextData.sponsoredData = res.data.sponsored.map((s: any) => ({
              label: s.label || "Sponsored",
              title: s.title,
              subtitle: s.subtitle,
              img: s.image ? `${CDN_BASE}${s.image}` : `${CDN_BASE}placeholder-car.webp`,
              redirect_url: s.url || "/"
            }));
          }
          if (res.data.trending?.length) {
            nextData.trendingData = res.data.trending.map((t: any) => t.title || t);
          }
        } else if (res.type === "suggestions") {
          nextData.step2Data.models = (res.data.models || []).map((m: any) => ({
            label: m.title,
            link: m.url,
            img: m.featured_image ? (m.featured_image.startsWith('http') ? m.featured_image : `${CDN_BASE}${m.featured_image}`) : ""
          }));

          nextData.sectionRanking = (res.data.section_ranking || []).map((section: any) => ({
            section: section.section,
            score: section.score,
            rank: section.rank,
            top_item_url: section.top_item_url,
            top_item_score: section.top_item_score,
          }));

          const utilityItems = res.data.utility || [];
          nextData.step2Data.utility = buildUtilityPricingItems(
            res.data.models || [],
            utilityItems,
            activeLocation,
            {
              price: initialData.icons.price,
              emi: initialData.icons.emi,
            }
          );

          nextData.step2Data.listings = (res.data.listings || []).map((l: any) => ({
            label: l.title,
            link: l.url,
            icon: initialData.icons.price
          }));

          nextData.step2Data.comparisons = (res.data.compare || []).map((c: any) => ({
            label: c.title,
            link: c.url,
          }));

          nextData.step2Data.editorial = (res.data.content || []).map((e: any) => {
            let icon = initialData.icons.web;
            if (e.page_type === "video") icon = initialData.icons.video;
            if (e.page_type === "news") icon = initialData.icons.news;
            return {
              label: e.title,
              link: e.url,
              icon
            };
          });
        }

        setDropdownData(nextData);
      } catch (e) {
        if (requestId === latestRequestIdRef.current) {
          console.error("Error in SearchHeader fetch", e);
        }
      } finally {
        if (!isCancelled && requestId === latestRequestIdRef.current) {
          setIsSearchLoading(false);
        }
      }
    };

    const timer = setTimeout(fetchSearch, 300);
    return () => {
      clearTimeout(timer);
      isCancelled = true;
    };
  }, [searchText, initialData, activeLocation]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (window.innerWidth < 768) return;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        blurActiveElement();
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [blurActiveElement]);

  useEffect(() => {
    if (!showDropdown) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      blurActiveElement();
      setShowDropdown(false);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [blurActiveElement, showDropdown]);

  return (
    <>
      {/* ================= Desktop ================= */}
      <div
        ref={wrapperRef}
        className="relative w-full max-w-xs md:max-w-md lg:max-w-lg xl:max-w-[283px] ml-[24px] mr-auto hidden md:block"
      >
        <div className="relative">
          <span className="absolute left-5 top-1/2 -translate-y-1/2 pointer-events-none">
            <ImageWrapper
              src="/search.svg"
              alt="Search"
              title="Search"
              width={18}
              height={18}
              cdnBaseUrl={`${CDN_ICON}`}
            />
          </span>

          <input
            type="text"
            placeholder="Search for Cars e.g., Maruti"
            value={searchText}
            onClick={() => setShowDropdown(true)}
            onChange={(e) => {
              handleSearchTextChange(e.target.value);
              setShowDropdown(true);
            }}
            className={`w-full h-[46px] pl-[44px] pr-12 rounded-full bg-[#F3F4F6] border border-[#D1D5DB] outline-none text-[#000] placeholder:text-[#717182] text-[14px] ${TRACKING.COMPONENT.SEARCH_INIT}`}
          />

          {(isSearchLoading || searchText.trim().length > 0) && (
            <span className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center">
              {isSearchLoading ? (
                <div className="animate-spin h-4 w-4 border-2 border-[#008C8B] border-t-transparent rounded-full" />
              ) : (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="p-1 bg-gray-400 rounded-full hover:bg-gray-500 transition-colors cursor-pointer"
                  aria-label="Clear search"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </span>
          )}

          {showDropdown && (
            <SearchDropdownRevamp
              query={searchText}
              isTyping={isUserTyping || isSearchLoading}
              data={dropdownData}
              onOptionClick={handleClose}
            />
          )}
        </div>
      </div>

      {/* ================= Mobile Icon ================= */}
      <span
        className="md:hidden p-2 text-gray-700 cursor-pointer"
        onClick={() => setShowDropdown(true)}
      >
        <Image
          src={`${CDN_ICON}search-icons/search-green-arrow.svg`}
          alt="Search"
          title="Search"
          width={24}
          height={24}
          style={{ filter: "invert(1) brightness(0)" }}
          className={DIWALI_HEADER.mobileSearchIcon}
        />
      </span>

      {/* ================= Mobile Fullscreen UI ================= */}
      {showDropdown && (
        <div className="fixed inset-0 bg-white z-50 flex flex-col md:hidden">
          <MobileSearchHeader
            searchText={searchText}
            onChange={handleSearchTextChange}
            isLoading={isSearchLoading}
            onClear={handleClearSearch}
            onClose={handleClose}
          />

          <div className="overflow-y-auto no-scrollbar">
            <SearchDropdownRevamp
              query={searchText}
              isMobile
              isTyping={isUserTyping || isSearchLoading}
              data={dropdownData}
              onOptionClick={handleClose}
            />
          </div>
        </div>
      )}
    </>
  );
}